import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { withRouter } from 'react-router-dom';
import moment from 'moment';

import { useTheme } from '@material-ui/core/styles';

import { getFormat, under2camel } from 'lib/commonLib';
import { pieGraphMedia } from 'lib/api/time';

import CurrentStatus from 'components/atoms/CurrentStatus';
import ContentsWrap from 'components/molecules/ContentsWrap';
import InfoTitleArea from 'components/molecules/MbTable/InfoTitleArea';
import Highchart from 'components/organisms/Chart/Highchart';
import SwitchingArea from 'components/organisms/SwitchingArea';

const StatisticsGraph = (props) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { locale, params, handleClickSnackbar } = props;
  //하이차트 기본설정
  const initOptions = {
    chart: {
      type: 'pie',
      backgroundColor: theme.palette.common.bgColor,
    },
    title: {
      text: '',
    },
    credits: {
      enabled: false,
    },
    xAxis: {
      type: 'category',
    },
    legend: {
      reversed: true,
    },
    tooltip: {
      pointFormat: '{series.name}: <b>{point.percentage:.1f}%</b>',
    },
    plotOptions: {
      pie: {
        allowPointSelect: true,
        cursor: 'pointer',
        dataLabels: {
          enabled: true,
          format: '<b>{point.name}</b>: {point.percentage:.1f} %',
        },
      },
    },
    series: [],
  };
  const initViewState = {
    ADVRTS_AMT: 'loading',
    CLICK_CNT: 'loading',
    PAR_EPRS_CNT: 'loading',
    SESSION_ROAS: 'loading',
  };
  const [optionList, setOptionList] = useState(initOptions);
  const [infoMessage, setInfoMessage] = useState('');
  const [viewState, setViewState] = useState(initViewState);
  const [loading, setLoading] = useState(false);
  const [prevParams, setPrevParams] = useState({});
  const graphKey = ['ADVRTS_AMT', 'CLICK_CNT', 'PAR_EPRS_CNT', 'SESSION_ROAS'];

  useEffect(() => {
    if (JSON.stringify(params) !== JSON.stringify(prevParams)) {
      getData();
      setPrevParams(params);
    }
    // eslint-disable-next-line
  }, [params]);

  /**
   * 그래프 데이터를 받아온다.
   */
  const getData = () => {
    const { sDate, eDate } = params;
    const format = getFormat(locale);
    setViewState(initViewState);
    setLoading(true);
    setInfoMessage(
      t('까지의 통계 그래프입니다.', {
        date: t('term', {
          startDate: moment(sDate).format(format),
          endDate: moment(eDate).format(format),
        }),
      })
    );
    pieGraphMedia(params)
      .then((response) => {
        const { data } = response.data;
        makeGraphData(data);
        let tmpStatus = { ...viewState };
        tmpStatus = {
          ...tmpStatus,
          ADVRTS_AMT: data.ADVRTS_AMT.total === 0 ? 'noData' : '',
          CLICK_CNT: data.CLICK_CNT.total === 0 ? 'noData' : '',
          PAR_EPRS_CNT: data.PAR_EPRS_CNT.total === 0 ? 'noData' : '',
          SESSION_ROAS: data.SESSION_ROAS.total === 0 ? 'noData' : '',
        };
        setViewState(tmpStatus);
      })
      .catch((error) => {
        handleClickSnackbar({
          message: error,
          option: { type: 'error' },
        });
        setViewState({
          ADVRTS_AMT: 'error',
          CLICK_CNT: 'error',
          PAR_EPRS_CNT: 'error',
          SESSION_ROAS: 'error',
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  /**
   * 그래프용 데이터 가공
   */
  const makeGraphData = (data) => {
    let tmpSeries = {
      ADVRTS_AMT: [],
      CLICK_CNT: [],
      PAR_EPRS_CNT: [],
      SESSION_ROAS: [],
    };
    let newOptions = {
      advrtsAmt: {},
      clickCnt: {},
      parEprsCnt: {},
      sessionRoas: {},
    };
    // eslint-disable-next-line
    graphKey.map((key) => {
      data[key].data.forEach((item) => {
        tmpSeries[key].push({
          name: item.name,
          y: item.value,
        });
      });
    });

    newOptions = {
      ...newOptions,
      advrtsAmt: {
        ...initOptions,
        title: { text: t('소진') },
        series: [
          {
            name: t('소진'),
            colorByPoint: true,
            data: tmpSeries.ADVRTS_AMT,
          },
        ],
      },
      clickCnt: {
        ...initOptions,
        title: { text: t('클릭수') },
        series: [
          {
            name: t('클릭수'),
            colorByPoint: true,
            data: tmpSeries.CLICK_CNT,
          },
        ],
      },
      parEprsCnt: {
        ...initOptions,
        title: { text: t('노출량') },
        series: [
          {
            name: t('노출량'),
            colorByPoint: true,
            data: tmpSeries.PAR_EPRS_CNT,
          },
        ],
      },
      sessionRoas: {
        ...initOptions,
        title: { text: 'ROAS' },
        series: [
          {
            name: 'ROAS',
            colorByPoint: true,
            data: tmpSeries.SESSION_ROAS,
          },
        ],
      },
    };
    setOptionList(newOptions);
  };

  return (
    <SwitchingArea
      title={
        <InfoTitleArea
          title={t('매체분포도')}
          tooltipTitle={infoMessage}
          iconType="fainfoCircle"
          iconColor="graph"
        />
      }
      isOpen={false}
    >
      <ContentsWrap contentsCount={2} loading={loading}>
        {graphKey.map((item, index) => {
          return (
            <div key={index}>
              {!!optionList[under2camel(item)] ? (
                <Highchart
                  options={optionList[under2camel(item)]}
                  status={viewState[item]}
                />
              ) : (
                <CurrentStatus type="noData" option="useMinHeight" />
              )}
            </div>
          );
        })}
      </ContentsWrap>
    </SwitchingArea>
  );
};

export default withRouter(StatisticsGraph);
